/**
 * useEmailAccounts Hook
 *
 * React hook for managing email accounts with real-time Firestore updates.
 * Provides state management for listing, creating, updating, and deleting email accounts.
 */

import { notifications } from "@mantine/notifications";
import { useEffect, useState, useCallback } from "react";
import {
  subscribeToEmailAccounts,
  createEmailAccount as createEmailAccountFirestore,
  deleteEmailAccount as deleteEmailAccountFirestore,
  setDefaultEmailAccount as setDefaultEmailAccountFirestore
} from "../firestore";
import type { EmailAccount, EmailAccountFormData } from "../schema/email-account.types";

/**
 * Hook state interface
 */
interface UseEmailAccountsResult {
  /** Array of email accounts */
  accounts: EmailAccount[];
  /** Loading state (initial load) */
  loading: boolean;
  /** Error state */
  error: Error | null;
  /** Default email account (if any) */
  defaultAccount: EmailAccount | null;
  /** Refresh accounts manually */
  refresh: () => void;
  /** Create new email account */
  createAccount: (data: EmailAccountFormData) => Promise<string>;
  /** Delete email account (soft delete) */
  deleteAccount: (accountId: string) => Promise<void>;
  /** Set email account as default */
  setAsDefault: (accountId: string) => Promise<void>;
}

/**
 * useEmailAccounts Hook
 *
 * Manages email accounts for the current licenciada with real-time updates.
 * @param licenciadaId - Licenciada ID to fetch accounts for
 * @param onlyActive - Filter only active accounts (default: true)
 * @returns Hook result with accounts, loading state, and actions
 * @example
 * function EmailAccountsList() {
 *   const { accounts, loading, createAccount, deleteAccount } = useEmailAccounts("lic123");
 *
 *   if (loading) return <Loader />;
 *
 *   return (
 *     <div>
 *       {accounts.map(account => (
 *         <div key={account.id}>{account.email}</div>
 *       ))}
 *     </div>
 *   );
 * }
 */
export function useEmailAccounts(licenciadaId: string, onlyActive: boolean = true): UseEmailAccountsResult {
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!licenciadaId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToEmailAccounts(licenciadaId, onlyActive, (newAccounts) => {
      setAccounts(newAccounts);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => {
      unsubscribe();
    };
  }, [licenciadaId, onlyActive, refreshTrigger]);

  // Get default account
  const defaultAccount = accounts.find((account) => account.is_default) || null;

  // Manual refresh
  const refresh = useCallback(() => {
    setRefreshTrigger((previous) => previous + 1);
  }, []);

  // Create new email account
  const createAccount = useCallback(
    async (data: EmailAccountFormData): Promise<string> => {
      try {
        return await createEmailAccountFirestore(licenciadaId, data);
      } catch (error_) {
        console.error("Failed to create email account:", error_);

        const errorMessage = error_ instanceof Error ? error_.message : "Erro ao criar conta de email";

        notifications.show({
          title: "Erro",
          message: errorMessage,
          color: "red"
        });

        throw error_;
      }
    },
    [licenciadaId]
  );

  // Delete email account
  const deleteAccount = useCallback(async (accountId: string): Promise<void> => {
    try {
      await deleteEmailAccountFirestore(accountId);
    } catch (error_) {
      console.error("Failed to delete email account:", error_);

      const errorMessage = error_ instanceof Error ? error_.message : "Erro ao remover conta de email";

      notifications.show({
        title: "Erro",
        message: errorMessage,
        color: "red"
      });

      throw error_;
    }
  }, []);

  // Set as default email account
  const setAsDefault = useCallback(async (accountId: string): Promise<void> => {
    try {
      await setDefaultEmailAccountFirestore(accountId);

      notifications.show({
        title: "Conta padrão atualizada",
        message: "Email account definido como padrão",
        color: "green"
      });
    } catch (error_) {
      console.error("Failed to set default email account:", error_);

      const errorMessage = error_ instanceof Error ? error_.message : "Erro ao definir conta padrão";

      notifications.show({
        title: "Erro",
        message: errorMessage,
        color: "red"
      });

      throw error_;
    }
  }, []);

  return {
    accounts,
    loading,
    error,
    defaultAccount,
    refresh,
    createAccount,
    deleteAccount,
    setAsDefault
  };
}
